<template>
  <div id="app">
    <template v-if="!isDashboardRoute && !isLandingRoute && !isVendorPortalRoute">
      <nav class="app_navbar" v-if="isLoggedIn">
        <div class="app_navbar-container">
          <router-link class="app_navbar-brand" to="/home" title="AnnaBooks Home">
            <img src="@/assets/annabooks-logo.png" alt="AnnaBooks" class="app_navbar-brand-logo" />
          </router-link>
          <div class="app_navbar-actions">
            <button
              v-if="$route.path === '/home' && !isInvitedUser"
              type="button"
              class="app_navbar-btn app_navbar-btn--create"
              @click="createNewCompany"
            >
              <i class="bi bi-building-add" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Create New Company</span>
            </button>
            <button
              v-if="$route.path === '/home' && !isInvitedUser"
              type="button"
              class="app_navbar-btn app_navbar-btn--outline"
              @click="showCompanyImport"
            >
              <i class="bi bi-upload" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Import</span>
            </button>
            <button
              v-if="$route.path === '/home' && !isInvitedUser"
              type="button"
              class="app_navbar-btn app_navbar-btn--outline"
              @click="showEncryptionSettings"
            >
              <i class="bi bi-shield-lock" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Encryption</span>
            </button>
            <button
              v-if="$route.path === '/home' && !isInvitedUser"
              type="button"
              class="app_navbar-btn app_navbar-btn--danger"
              @click="showDeleteAccount"
            >
              <i class="bi bi-person-x" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Delete Account</span>
            </button>
            <button
              v-if="$route.path === '/home'"
              type="button"
              class="app_navbar-btn app_navbar-btn--logout"
              @click="handleLogout"
            >
              <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Logout</span>
            </button>
            <button
              v-if="$route.path.startsWith('/dashboard')"
              type="button"
              class="app_navbar-btn app_navbar-btn--outline"
              @click="showHelp"
            >
              <i class="bi bi-question-circle" aria-hidden="true"></i>
              <span class="app_navbar-btn-text">Help</span>
            </button>
          </div>
        </div>
      </nav>
    </template>
    
    <!-- Shimmer Loader for Route Transitions -->
    <ShimmerLoader :show="$store.state.loading" :route="$route" />
    
    <router-view :key="routeViewKey" />
    <ToastContainer />
    <AccountDeactivationNotice v-if="isLoggedIn && !isManagerOrAdminRoute" />

    <Transition name="mascot-assistant-fade">
      <div
        v-if="mascotAssistantOpen && isLoggedIn && !isDashboardRoute && !isLandingRoute && !isVendorPortalRoute"
        class="mascot-assistant-overlay"
        :class="{ 'mascot-assistant-overlay--shared-view': isDashboardRoute }"
        role="presentation"
      >
        <div
          v-if="!isDashboardRoute"
          class="mascot-assistant-backdrop"
          aria-hidden="true"
        />
        <div
          class="mascot-assistant-floating mascot-assistant-floating--mascot-only"
          :class="{ 'mascot-assistant-floating--shared-view': isDashboardRoute }"
          role="dialog"
          aria-modal="true"
          :aria-label="mascotAssistantOverlayAriaLabel"
          @click.stop
        >
          <AssistantMascot
            mascot-only
            :active="false"
            :viewer-name="userName"
          />
        </div>
      </div>
    </Transition>
    
    <!-- Delete Account Modal -->
    <DeleteAccountModal
      :show="showDeleteAccountModal"
      :loading="isDeletingAccount"
      @confirm="confirmDeleteAccount"
      @cancel="showDeleteAccountModal = false"
    />

    <HomeCompanyImportModal
      :show="showCompanyImportModal"
      @close="showCompanyImportModal = false"
      @imported="onCompanyImported"
    />
    
    <!-- Logout Transition -->
    <AppTransition 
      :show="showLogoutTransition" 
      type="logout"
      title="Logging Out..."
      submessage="Thank you for using Anna Books"
    />
  </div>
</template>

<script>
import ToastContainer from './components/ToastContainer.vue';
import logger from '@/utils/logger';
import AppTransition from './components/transitions/AppTransition.vue';
import ShimmerLoader from './components/ShimmerLoader.vue';
import DeleteAccountModal from './components/DeleteAccountModal.vue';
import HomeCompanyImportModal from './components/HomeCompanyImportModal.vue';
import AssistantMascot from '@/components/AssistantMascot.vue';
import AccountDeactivationNotice from '@/components/AccountDeactivationNotice.vue';
import uiModeService from '@/utils/uiMode.service';
import uiPreferencesService from '@/utils/uiPreferences.service';
import { useMascotAssistant } from '@/composables/useMascotAssistant';

export default {
  name: 'App',
  components: {
    ToastContainer,
    AppTransition,
    ShimmerLoader,
    DeleteAccountModal,
    HomeCompanyImportModal,
    AssistantMascot,
    AccountDeactivationNotice
  },
  setup() {
    const {
      isOpen: mascotAssistantOpen,
      toggle: toggleMascotAssistant,
      close: closeMascotAssistant,
    } = useMascotAssistant();
    return {
      mascotAssistantOpen,
      toggleMascotAssistant,
      closeMascotAssistant,
    };
  },
  data() {
    return {
      showLogoutTransition: false,
      showDeleteAccountModal: false,
      showCompanyImportModal: false,
      isDeletingAccount: false,
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.state.auth.isLoggedIn
    },
    isInvitedUser() {
      const user = this.$store.state.auth.user;
      if (user && user.is_invited_user === true) return true;
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        return stored && stored.is_invited_user === true;
      } catch (_) {
        return false;
      }
    },
    userName() {
      return this.$store.state.auth.user?.firstName || 'User'
    },
    currentCompanyId() {
      return this.$store.state.currentCompany?.id || '';
    },
    isLandingRoute() {
      return (
        this.$route.meta.publicLanding === true ||
        this.$route.matched.some((record) => record.meta.publicAuthShell === true)
      )
    },
    isVendorPortalRoute() {
      return this.$route.matched.some((record) => record.meta.isVendorPortal)
    },
    routeViewKey() {
      if (['Terms', 'Privacy', 'Support'].includes(this.$route.name)) {
        return 'legal-shell'
      }
      if (['MainHome', 'Login', 'Signup', 'VendorLogin'].includes(this.$route.name)) {
        return 'public-auth-shell'
      }
      if (this.isVendorPortalRoute) {
        return 'vendor-portal-shell'
      }
      return undefined
    },
    isDashboardRoute() {
      return (this.$route.path.startsWith('/company/') && this.$route.path.includes('/dashboard')) ||
             this.$route.path.startsWith('/anna-admin/') ||
             this.$route.path.startsWith('/admin/') ||
             this.$route.path.startsWith('/client-manager/');
    },
    isManagerOrAdminRoute() {
      // Check if current route is Manager or Admin module
      return this.$route.path.startsWith('/anna-admin/') ||
             this.$route.path.startsWith('/admin/') ||
             this.$route.path.startsWith('/client-manager/');
    },
    mascotAssistantOverlayAriaLabel() {
      if (typeof navigator === 'undefined') {
        return 'Quick assistant. Press Control slash (or Control Shift slash) to close.';
      }
      const p = navigator.platform || '';
      const mac =
        /Mac|iPhone|iPad|iPod/i.test(p) ||
        (navigator.userAgentData && /mac/i.test(String(navigator.userAgentData.platform || '')));
      return mac
        ? 'Quick assistant. Press Command slash (or Command Shift slash) to close.'
        : 'Quick assistant. Press Control slash (or Control Shift slash) to close.';
    },
  },
  created() {
    // Apply cached UI preferences on app startup (only if not on Manager/Admin routes)
    if (!this.isManagerOrAdminRoute) {
      uiPreferencesService.applyCached();
    } else {
      // Remove compact mode if on Manager/Admin routes
      this.removeUIMode();
    }
    window.addEventListener('uiModeChanged', this.handleUIModeChange);
  },
  watch: {
    '$route': {
      handler: 'handleRouteChange',
      immediate: false,
    },
    isLoggedIn() {
      this.syncPresenceConnection();
    },
    isManagerOrAdminRoute() {
      this.syncPresenceConnection();
    },
  },
  mounted() {
    // Add global keyboard shortcut for toggling UI mode
    document.addEventListener('keydown', this.handleKeyboardShortcut, true);
    this.syncPresenceConnection();

    // Non-reactive dedup cache: `${clientId}:${action}` -> epoch-ms of last toast.
    // Stops reconnect / multi-tab noise from spamming the owner with the same event.
    this._userNotifyDedup = Object.create(null);
    this._boundUserNotification = this.handleUserActivityNotification.bind(this);
    window.addEventListener('company-user-notification', this._boundUserNotification);
  },
  beforeUnmount() {
    window.removeEventListener('uiModeChanged', this.handleUIModeChange);
    document.removeEventListener('keydown', this.handleKeyboardShortcut, true);
    if (this._boundUserNotification) {
      window.removeEventListener('company-user-notification', this._boundUserNotification);
      this._boundUserNotification = null;
    }
    import('@/services/companyPresenceClient').then((m) => m.disconnectCompanyPresence()).catch(() => {});
  },
  methods: {
    /**
     * Owner-facing toast for invited-user activity (login / logout / online /
     * offline). The presence WebSocket fans these out to everyone in the
     * company room — we filter on the client so only the owner sees them.
     *
     * Persistent records (with full timestamps + company context) are written
     * by the backend to CompanyActivityLog and are visible from the Users
     * activity logs view.
     */
    handleUserActivityNotification(e) {
      try {
        const payload = (e && e.detail) || {};
        const action = String(payload.action || '').toLowerCase();
        const clientId = Number(payload.clientId);
        if (!action || !Number.isFinite(clientId)) return;

        // Only owners receive toasts. Invited users don't get pinged about
        // each other (or themselves).
        if (this.isInvitedUser) return;

        const me = this.$store.state.auth.user;
        const myId = me && Number(me.id);
        if (Number.isFinite(myId) && myId === clientId) return;

        // Dedup the same (clientId, action) within 8 seconds so reconnect-noise
        // doesn't produce repeat toasts.
        if (!this._userNotifyDedup) this._userNotifyDedup = Object.create(null);
        const key = `${clientId}:${action}`;
        const now = Date.now();
        const last = this._userNotifyDedup[key] || 0;
        if (now - last < 8000) return;
        this._userNotifyDedup[key] = now;

        const name = payload.fullName || payload.email || `User #${clientId}`;
        let message;
        let kind = 'info';
        switch (action) {
          case 'login':
            message = `${name} signed in`;
            kind = 'success';
            break;
          case 'logout':
            message = `${name} signed out`;
            kind = 'info';
            break;
          case 'online':
            message = `${name} is online`;
            kind = 'success';
            break;
          case 'offline':
            message = `${name} went offline`;
            kind = 'warning';
            break;
          default:
            message = `${name}: ${action}`;
        }

        if (this.$toast && typeof this.$toast[kind] === 'function') {
          this.$toast[kind](message);
        } else if (this.$toast && typeof this.$toast.info === 'function') {
          this.$toast.info(message);
        }
      } catch (err) {
        logger.warn('handleUserActivityNotification failed:', err);
      }
    },
    syncPresenceConnection() {
      if (typeof window === 'undefined') return;
      import('@/services/companyPresenceClient')
        .then((m) => {
          if (this.isLoggedIn && !this.isManagerOrAdminRoute) {
            m.connectCompanyPresence();
          } else {
            m.disconnectCompanyPresence();
          }
        })
        .catch(() => {});
    },
    handleUIModeChange() {
      // Re-apply mode when changed from UI Preferences component or keyboard shortcut
      // But skip if on Manager/Admin routes
      if (this.isManagerOrAdminRoute) {
        this.removeUIMode();
      } else {
        uiModeService.applyCurrentMode();
      }
    },
    handleKeyboardShortcut(event) {
      const isSlashKey =
        event.key === '/' ||
        event.key === '?' ||
        event.key === 'Divide' ||
        event.code === 'Slash' ||
        event.code === 'NumpadDivide';
      const hasPrimaryMod = event.ctrlKey || event.metaKey;

      // Ctrl/Cmd + / (or Shift + /): Toggle ANNA AGENT assistant
      if (hasPrimaryMod && isSlashKey) {
        if (!this.isLoggedIn || this.isLandingRoute) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.toggleMascotAssistant();
        return;
      }
      // Ctrl/Cmd + Shift + L: Toggle UI mode
      // Support both Ctrl (Windows/Linux) and Cmd (Mac)
      // Check for both uppercase and lowercase 'L' to handle different keyboard states
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'L' || event.key === 'l')) {
        // Don't trigger when typing in input fields
        if (event.target.tagName === 'INPUT' || 
            event.target.tagName === 'TEXTAREA' || 
            event.target.contentEditable === 'true') {
          return;
        }
        
        // Don't trigger on Manager/Admin routes
        if (this.isManagerOrAdminRoute) {
          return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        this.toggleUIMode();
      }
    },
    handleRouteChange(to, from) {
      // Remove UI mode when navigating to Manager/Admin routes
      // Re-apply when navigating away from Manager/Admin routes
      if (to.path.startsWith('/anna-admin/') || 
          to.path.startsWith('/admin/') || 
          to.path.startsWith('/client-manager/')) {
        this.removeUIMode();
      } else if (from && (from.path.startsWith('/anna-admin/') || 
                          from.path.startsWith('/admin/') || 
                          from.path.startsWith('/client-manager/'))) {
        // Re-apply mode when navigating away from Manager/Admin routes
        uiModeService.applyCurrentMode();
      }
    },
    removeUIMode() {
      // Remove UI mode attributes from body and app element
      const body = document.body;
      const app = document.getElementById('app');
      if (body) body.removeAttribute('data-ui-mode');
      if (app) app.removeAttribute('data-ui-mode');
    },
    toggleUIMode() {
      try {
        const newMode = uiModeService.toggleMode();
        
        // Show toast notification if available
        if (this.$toast) {
          this.$toast.success(`UI mode switched to ${uiModeService.getModeLabel(newMode)}`);
        }
      } catch (error) {
        logger.error('Error toggling UI mode:', error);
      }
    },
    createNewCompany() {
      // Set store state to show welcome company screen in Home view
      this.$store.commit('SET_SHOW_WELCOME_COMPANY', true);
    },
    showCompanyImport() {
      this.showCompanyImportModal = true;
    },
    async onCompanyImported(company) {
      try {
        await this.$store.dispatch('companies/fetchCompanies');
      } catch (e) {
        logger.warn('Failed to refresh companies after import:', e?.message || e);
      }
      if (this.$toast) {
        const name = company?.name ? `“${company.name}”` : 'Company';
        this.$toast.success(`${name} imported successfully.`);
      }
      this.showCompanyImportModal = false;
    },
    showEncryptionSettings() {
      this.$router.push('/encryption-settings')
    },
    showDeleteAccount() {
      this.showDeleteAccountModal = true;
    },
    async confirmDeleteAccount() {
      try {
        this.isDeletingAccount = true;
        // TODO: Implement actual account deletion API call
        // await AccountService.deleteAccount();
        
        // For now, show a message
        if (this.$toast) {
          this.$toast.info('Account deletion feature is coming soon.');
        }
        
        // Close modal after a delay
        setTimeout(() => {
          this.showDeleteAccountModal = false;
          this.isDeletingAccount = false;
        }, 1500);
      } catch (error) {
        logger.error('Error deleting account:', error);
        if (this.$toast) {
          this.$toast.error('Failed to delete account. Please try again later.');
        }
        this.isDeletingAccount = false;
      }
    },
    async handleLogout() {
      try {
        // Show logout transition
        this.showLogoutTransition = true;
        
        // Wait for transition animation then logout
        setTimeout(async () => {
          await this.$store.dispatch('auth/logout');
          
          // Navigate to login after logout
          this.$router.push('/login');
          
          // Hide transition after navigation
          setTimeout(() => {
            this.showLogoutTransition = false;
          }, 300);
        }, 2000); // 2 second delay for smooth transition
      } catch (error) {
        logger.error('Logout error:', error);
        // Still try to redirect to login even if there's an error
        this.showLogoutTransition = false;
        this.$router.push('/login');
      }
    },
    showHelp() {
      this.$router.push('/help');
    }
  }
}
</script>

<style src="./App.css"></style> 